import { Fragment, MouseEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Popover, PopoverProps, Typography } from '@mui/material';

import { DataTree, IconButton, IconOption } from '@components/base';

export type RawDataPopoverProps<T extends object> = Pick<PopoverProps, 'onClose' | 'title'> & {
  anchorEl?: HTMLButtonElement;
  context?: 'item' | 'menu' | 'page' | 'rootMenu' | 'section';
  data?: T;
};

export function RawDataPopover<T extends object>({
  anchorEl,
  context,
  data,
  onClose,
  title,
}: RawDataPopoverProps<T>) {
  const { t } = useTranslation();

  return data ? (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      onClick={e => e.stopPropagation()}
      onClose={onClose}
      open={!!anchorEl}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box maxHeight={600} maxWidth={500}>
        <Typography pt={1} px={2} variant='h3'>
          {title || t('raw', { context })}
        </Typography>

        <DataTree data={data} disableControls initExpandAll language='json' />
      </Box>
    </Popover>
  ) : null;
}

type RawDataPopoverButtonProps<T extends object> = Pick<
  RawDataPopoverProps<T>,
  'context' | 'data' | 'onClose'
> & {
  icon?: IconOption;
  onOpen?: MouseEventHandler<HTMLButtonElement>;
  title?: string;
};

export function RawDataPopoverButton<T extends object>({
  context,
  data,
  icon = 'dataObject',
  onClose,
  onOpen,
  title,
}: RawDataPopoverButtonProps<T>) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();

  return (
    <Fragment>
      <IconButton
        disabled={!data}
        icon={icon}
        onClick={e => {
          onOpen?.(e);
          setAnchorEl(e.currentTarget);
        }}
        title={title || t('raw', { context })}
      />

      <RawDataPopover
        anchorEl={anchorEl}
        context={context}
        data={data}
        onClose={(event, reason) => {
          onClose?.(event, reason);
          setAnchorEl(undefined);
        }}
        title={title}
      />
    </Fragment>
  );
}
